import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Button, FormControl, TableCard } from '../ui';
import BackHomeButton from '../Common/BackHomeButton';
import {
  ORIGINATION_COMPLETABLE_STATUSES,
  ORIGINATION_CONTINUABLE_STATUSES,
  ORIGINATION_DISCARDABLE_STATUSES
} from '../../config/originationBoardColumns';
import { discardApprovedRequest, fetchRequests, markRejectedRequestsSeen } from '../../services/comersRequestFlow';
import { showRequestBlockedReason } from '../../utils/guardRequestContinuation';
import { REJECTION_REASON_STATUSES } from '../../utils/requestStatusGuard';
import RequestObservationsModal from './RequestObservationsModal';
import '../../styles/requests.css';
import '../../styles/approved-requests.css';

const MySwal = withReactContent(Swal);

const CONTINUABLE_STATUSES = ORIGINATION_CONTINUABLE_STATUSES;
const COMPLETABLE_STATUSES = ORIGINATION_COMPLETABLE_STATUSES;
const DISCARDABLE_STATUSES = ORIGINATION_DISCARDABLE_STATUSES;

const OBSERVATION_OWNER_FILTERS = {
  all: { value: 'all', label: 'Todos' },
  external: { value: 'external', label: 'Comercio' },
  internal: { value: 'internal', label: 'Financiera' }
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const getStatusPillClassName = (status) => {
  const s = Number(status);
  const map = {
    2: 'approved-requests-page__status-pill--s2',
    14: 'approved-requests-page__status-pill--s14',
    8: 'approved-requests-page__status-pill--s8',
    11: 'approved-requests-page__status-pill--s11',
    20: 'approved-requests-page__status-pill--s20',
    9: 'approved-requests-page__status-pill--s9',
    21: 'approved-requests-page__status-pill--s9'
  };
  return map[s] || 'approved-requests-page__status-pill--default';
};

const getVisibleTableColumns = (isClientScopedView) => {
  const columns = [
    {
      id: 'requestId',
      label: 'Solicitud',
      cellClassName: 'approved-requests-page__cell-request-id text-nowrap',
      renderSkeleton: () => <Skeleton width={56} height={16} />,
      renderCell: (request) => (request.id != null ? `#${request.id}` : '—')
    },
    {
      id: 'customer',
      label: 'Cliente',
      omitInClientScopedView: true,
      renderSkeleton: () => <Skeleton width={160} height={16} />,
      renderCell: (request) => (
        <div className="approved-requests-page__cell-name">{request.customer}</div>
      )
    },
    {
      id: 'dni',
      label: 'DNI',
      cellClassName: 'approved-requests-page__cell-muted',
      omitInClientScopedView: true,
      renderSkeleton: () => <Skeleton width={84} height={14} />,
      renderCell: (request) => request.dni
    },
    {
      id: 'status',
      label: 'Estado',
      renderSkeleton: () => <Skeleton width={118} height={28} borderRadius={999} />,
      renderCell: (request) => (
        <>
          <span className={`approved-requests-page__status-pill ${getStatusPillClassName(request.status)}`}>
            {request.status_label || '—'}
          </span>
          {request.relevant_observation?.description && (
            <div className="approved-requests-page__observation-preview">
              {request.relevant_observation.description}
            </div>
          )}
        </>
      )
    },
    {
      id: 'amount',
      label: 'Monto / cuotas',
      headerClassName: 'text-end',
      cellClassName: 'text-end',
      renderSkeleton: () => (
        <div className="requests-page__cell-stack approved-requests-page__cell-stack--end">
          <Skeleton width={104} height={16} />
          <Skeleton width={84} height={12} />
        </div>
      ),
      renderCell: (request) => (
        <>
          <div className="approved-requests-page__amount-main">
            {formatCurrency(request.offer?.amount)}
          </div>
          <div className="approved-requests-page__amount-sub">
            {request.offer?.source === 'pre_offer' ? 'Preoferta · ' : ''}
            {request.offer?.installments || 0} x {formatCurrency(request.offer?.installment_amount)}
          </div>
        </>
      )
    },
    {
      id: 'updatedAt',
      label: 'Actualización',
      cellClassName: 'approved-requests-page__cell-muted text-nowrap',
      renderSkeleton: () => <Skeleton width={112} height={14} />,
      renderCell: (request) => formatDateTime(request.updated_at)
    },
    {
      id: 'actions',
      label: 'Acciones',
      headerClassName: 'text-end',
      renderSkeleton: ({ withActions }) => (
        withActions ? (
          <div className="requests-page__actions approved-requests-page__actions-cell">
            <Skeleton circle width={34} height={34} />
            <Skeleton circle width={34} height={34} />
          </div>
        ) : (
          <Skeleton width={32} height={16} />
        )
      ),
      renderCell: (request, { renderActionCell }) => renderActionCell(request)
    }
  ];

  return isClientScopedView
    ? columns.filter((column) => !column.omitInClientScopedView)
    : columns;
};

const LoadingRows = ({ columns, rows = 6, withActions = true }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr key={`loading-row-${index}`} className="approved-requests-page__row-skeleton">
        {columns.map((column) => (
          <td key={column.id} data-label={column.label} className={column.cellClassName || undefined}>
            {column.renderSkeleton({ withActions })}
          </td>
        ))}
      </tr>
    ))}
  </>
);

const RequestsExplorer = ({
  preset = null,
  customerDocument = '',
  embedded = false,
  onResumeApprovedRequest = null
}) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [observationOwnerFilter, setObservationOwnerFilter] = useState(OBSERVATION_OWNER_FILTERS.all.value);
  const [presetEnabled, setPresetEnabled] = useState(Boolean(preset?.statuses?.length));
  const [selectedRequestForObservations, setSelectedRequestForObservations] = useState(null);
  const [blockedReasonLoadingUid, setBlockedReasonLoadingUid] = useState(null);
  const isClientScopedView = Boolean(customerDocument);
  const visibleColumns = useMemo(
    () => getVisibleTableColumns(isClientScopedView),
    [isClientScopedView]
  );
  const tableColumnCount = visibleColumns.length;
  const searchPlaceholder = isClientScopedView
    ? 'Buscar por N° solicitud, UID o estado'
    : 'Buscar por nombre, DNI, N° solicitud, UID o estado';

  useEffect(() => {
    setPresetEnabled(Boolean(preset?.statuses?.length));
    setSearch('');
    setObservationOwnerFilter(OBSERVATION_OWNER_FILTERS.all.value);
  }, [preset?.key, customerDocument]);

  const loadRequests = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await fetchRequests({
        statuses: presetEnabled ? preset?.statuses || [] : [],
        customerDocument
      });
      setRequests(data);
    } catch (error) {
      console.error('Error loading requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerDocument, preset?.statuses, presetEnabled]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (loading || customerDocument || !presetEnabled || preset?.key !== 'rejected' || requests.length === 0) {
      return;
    }

    markRejectedRequestsSeen().catch((error) => {
      console.error('Error marking rejected requests as seen:', error);
    });
  }, [loading, customerDocument, presetEnabled, preset?.key, requests]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((request) => {
      if (
        preset?.key === 'observed'
        && observationOwnerFilter !== OBSERVATION_OWNER_FILTERS.all.value
        && request.relevant_observation?.action_owner !== observationOwnerFilter
      ) {
        return false;
      }

      if (!q) return true;
      const haystack = `${request.customer || ''} ${request.dni || ''} ${request.id || ''} ${request.uid || ''} ${request.status_label || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [observationOwnerFilter, preset?.key, requests, search]);

  const totalCount = requests.length;
  const filteredCount = filteredRequests.length;
  const hasSearch = Boolean(search.trim());
  const hasObservationOwnerFilter = preset?.key === 'observed' && observationOwnerFilter !== OBSERVATION_OWNER_FILTERS.all.value;
  const hasActiveFilters = Boolean(customerDocument || hasSearch || presetEnabled || hasObservationOwnerFilter);
  const pageTitle = customerDocument ? 'Solicitudes del cliente' : preset?.label || 'Solicitudes';
  const pageLead = customerDocument
    ? 'Consultá todas las solicitudes del cliente y, si querés, quitá el filtro inicial de estado para revisar su historial completo.'
    : preset?.lead || 'Consultá solicitudes del circuito comercial según el estado que necesites revisar.';
  const searchLabel = preset?.searchLabel || 'Buscar solicitudes';
  const searchHint = preset?.searchHint || 'Buscá por nombre, DNI, UID o estado.';
  const filterSummaryText = [
    presetEnabled && preset?.label ? preset.label : null,
    hasObservationOwnerFilter ? `Esperando acción de: ${OBSERVATION_OWNER_FILTERS[observationOwnerFilter]?.label}` : null,
    customerDocument ? `Cliente ${customerDocument}` : null,
    hasSearch ? `"${search.trim()}"` : null
  ].filter(Boolean).join(' · ') || 'Sin filtros activos';
  const emptyStateCopy = totalCount === 0
    ? (isClientScopedView
      ? 'Cuando este cliente tenga solicitudes en tu comercio o sucursal, aparecerán acá.'
      : 'Cuando existan solicitudes dentro de tu comercio o sucursal, aparecerán acá.')
    : (isClientScopedView
      ? 'Probá con otro N° de solicitud, UID o estado.'
      : 'Probá con otro nombre, DNI, número de solicitud, UID o quitá el filtro de estado para ampliar la búsqueda.');

  const canContinueRequest = (request) => CONTINUABLE_STATUSES.has(Number(request.status));
  const canCompleteRequest = (request) => COMPLETABLE_STATUSES.has(Number(request.status));
  const canDiscardRequest = (request) => DISCARDABLE_STATUSES.has(Number(request.status));
  const canViewRejectionReason = (request) => REJECTION_REASON_STATUSES.has(Number(request.status));

  const resumeRequestFlow = (request) => {
    if (isClientScopedView && typeof onResumeApprovedRequest === 'function') {
      onResumeApprovedRequest(request);
      return;
    }

    navigate(`/clientes/${request.dni}`, {
      state: {
        pendingAction: 'approved-request',
        approvedRequestUid: request.uid,
        resumeFlowVariant: request.resume_flow_variant || 'approved'
      }
    });
  };

  const handleContinue = (request) => {
    if (!canContinueRequest(request)) return;
    resumeRequestFlow(request);
  };

  const handleComplete = (request) => {
    if (!canCompleteRequest(request)) return;
    resumeRequestFlow({
      ...request,
      resume_flow_variant: request.resume_flow_variant || 'initial'
    });
  };

  const handleOpenObservations = (request) => {
    setSelectedRequestForObservations(request);
  };

  const handleViewRejectionReason = async (request) => {
    if (!request?.uid || blockedReasonLoadingUid) return;

    setBlockedReasonLoadingUid(request.uid);

    try {
      await showRequestBlockedReason(request.uid);
    } finally {
      setBlockedReasonLoadingUid(null);
    }
  };

  const handleDiscard = async (request) => {
    if (!canDiscardRequest(request)) return;

    const result = await MySwal.fire({
      icon: 'warning',
      title: '¿Descartar solicitud?',
      html: `Se marcará como <strong>oferta desistida</strong> para <strong>${request.customer || 'el cliente'}</strong> (DNI ${request.dni}). Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, descartar',
      cancelButtonText: 'Cancelar',
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    try {
      await discardApprovedRequest(request.uid);
      await MySwal.fire({
        icon: 'success',
        title: 'Solicitud descartada',
        timer: 2200,
        showConfirmButton: false
      });
      setRequests((prev) => prev.filter((item) => item.uid !== request.uid));
    } catch (error) {
      const msg = error.response?.data?.message || 'No se pudo descartar la solicitud.';
      MySwal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setObservationOwnerFilter(OBSERVATION_OWNER_FILTERS.all.value);
    setPresetEnabled(false);
  };

  const restorePreset = () => {
    if (!preset?.statuses?.length) return;
    setPresetEnabled(true);
  };

  const renderActionCell = (request) => {
    const canContinue = canContinueRequest(request);
    const canComplete = canCompleteRequest(request);
    const canDiscard = canDiscardRequest(request);
    const canOpenObservations = Boolean(request.relevant_observation);
    const canShowRejectionReason = canViewRejectionReason(request);
    const isLoadingRejectionReason = blockedReasonLoadingUid === request.uid;

    if (!canContinue && !canComplete && !canDiscard && !canOpenObservations && !canShowRejectionReason) {
      return <span className="approved-requests-page__cell-muted">—</span>;
    }

    return (
      <div className="requests-page__actions approved-requests-page__actions-cell">
        {canShowRejectionReason && (
          <button
            type="button"
            className="approved-requests-page__icon-btn approved-requests-page__icon-btn--rejection"
            onClick={() => handleViewRejectionReason(request)}
            disabled={Boolean(blockedReasonLoadingUid)}
            title="Ver motivo del rechazo"
            aria-label={`Ver motivo del rechazo de la solicitud ${request.id || request.uid}`}
          >
            <i
              className={`bi ${isLoadingRejectionReason ? 'bi-arrow-repeat rg-spin' : 'bi-info-circle'}`}
              aria-hidden
            />
          </button>
        )}
        {canOpenObservations && (
          <button
            type="button"
            className="approved-requests-page__icon-btn approved-requests-page__icon-btn--observation"
            onClick={() => handleOpenObservations(request)}
            title="Ver observaciones"
            aria-label={`Ver observaciones de ${request.customer || 'cliente'}`}
          >
            <i className="bi bi-chat-left-text" aria-hidden />
          </button>
        )}
        {canComplete && (
          <button
            type="button"
            className="approved-requests-page__icon-btn approved-requests-page__icon-btn--complete"
            onClick={() => handleComplete(request)}
            title="Completar documentación"
            aria-label={`Completar solicitud de ${request.customer || 'cliente'}`}
          >
            <i className="bi bi-pencil-square" aria-hidden />
          </button>
        )}
        {canContinue && (
          <button
            type="button"
            className="approved-requests-page__icon-btn approved-requests-page__icon-btn--continue"
            onClick={() => handleContinue(request)}
            title="Continuar flujo"
            aria-label={`Continuar solicitud de ${request.customer || 'cliente'}`}
          >
            <i className="bi bi-arrow-right" aria-hidden />
          </button>
        )}
        {canDiscard && (
          <button
            type="button"
            className="approved-requests-page__icon-btn approved-requests-page__icon-btn--discard"
            onClick={() => handleDiscard(request)}
            title="Descartar solicitud"
            aria-label={`Descartar solicitud de ${request.customer || 'cliente'}`}
          >
            <i className="bi bi-trash3" aria-hidden />
          </button>
        )}
      </div>
    );
  };

  const content = (
    <section className={embedded ? '' : 'section requests-page__section'}>
      <TableCard
        className="requests-page__table-card approved-requests-page__table-card"
        bodyClassName="requests-page__table-card-body"
        toolbarClassName="requests-page__toolbar"
        tableClassName="requests-page__table-wrap"
        header={isClientScopedView ? null : (
          <div className="requests-page__context-bar">
            <div className="requests-page__context-copy">
              <span className="requests-page__context-eyebrow">{preset?.eyebrow || 'Solicitudes'}</span>
              <p className="requests-page__context-text">
                {loading
                  ? 'Actualizando solicitudes...'
                  : `${filteredCount} solicitud${filteredCount === 1 ? '' : 'es'} visible${filteredCount === 1 ? '' : 's'} de ${totalCount}`}
              </p>
            </div>
            <div className="requests-page__context-actions">
              <span className="requests-page__context-pill">
                {filterSummaryText}
              </span>
            </div>
          </div>
        )}
        toolbar={(
          <div className="requests-page__filters approved-requests-page__filters">
            <div className="row g-3">
              <div className={preset?.key === 'observed' ? 'col-12 col-lg-6' : 'col-12 col-lg-8'}>
                <FormControl
                  id="requests-search"
                  name="search"
                  label={isClientScopedView ? null : searchLabel}
                  aria-label={isClientScopedView ? 'Buscar solicitudes' : undefined}
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  hint={isClientScopedView ? null : searchHint}
                  iconLeft={<i className="bi bi-search" aria-hidden />}
                />
              </div>
              {preset?.key === 'observed' && (
                <div className="col-12 col-lg-2">
                  <FormControl
                    as="select"
                    id="requests-observation-owner"
                    name="observation_owner"
                    label="Esperando acción de"
                    value={observationOwnerFilter}
                    onChange={(e) => setObservationOwnerFilter(e.target.value)}
                    hint="Filtrá por comercio o financiera."
                  >
                    {Object.values(OBSERVATION_OWNER_FILTERS).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormControl>
                </div>
              )}
              <div className="col-12 col-lg-4 requests-page__filters-actions-col">
                <div className="requests-page__filters-actions">
                  {preset?.statuses?.length > 0 && !presetEnabled && (
                    <Button
                      variant="ghost"
                      className="requests-page__filter-button"
                      onClick={restorePreset}
                    >
                      <i className="bi bi-funnel" aria-hidden />
                      Restaurar filtro
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="requests-page__filter-button"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters || Boolean(customerDocument && !presetEnabled && !hasSearch)}
                  >
                    <i className="bi bi-eraser" aria-hidden />
                    Quitar filtros
                  </Button>
                  <Button
                    variant="outlineSecondary"
                    className="requests-page__filter-button"
                    onClick={loadRequests}
                    disabled={refreshing}
                  >
                    <i className={`bi bi-arrow-clockwise${refreshing ? ' rg-spin' : ''}`} aria-hidden />
                    Actualizar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        table={(
          <table
            className={`table table-hover requests-page__table approved-requests-page__table${isClientScopedView ? ' approved-requests-page__table--client-scoped' : ''}`}
          >
            <thead>
              <tr>
                {visibleColumns.map((column) => (
                  <th key={column.id} scope="col" className={column.headerClassName || undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRows columns={visibleColumns} />
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} className="requests-page__empty-cell approved-requests-page__empty-cell">
                    <i
                      className={`bi ${totalCount === 0 ? 'bi-inbox' : 'bi-search'} requests-page__empty-icon`}
                      aria-hidden
                    />
                    <div className="requests-page__empty-title">
                      {totalCount === 0 ? 'No hay solicitudes para mostrar' : 'No encontramos coincidencias'}
                    </div>
                    <p className="requests-page__empty-copy">
                      {emptyStateCopy}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <tr key={request.uid}>
                    {visibleColumns.map((column) => (
                      <td
                        key={column.id}
                        data-label={column.label}
                        className={column.cellClassName || undefined}
                      >
                        {column.renderCell(request, { renderActionCell })}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      />
    </section>
  );

  if (embedded) {
    return (
      <>
        {content}
        <RequestObservationsModal
          open={Boolean(selectedRequestForObservations)}
          request={selectedRequestForObservations}
          onClose={() => setSelectedRequestForObservations(null)}
        />
      </>
    );
  }

  return (
    <>
      <div className="page-content requests-page approved-requests-page rg-shell-content">
        <header className="requests-page__heading">
          <div>
            <h1 className="requests-page__title">{pageTitle}</h1>
            <p className="requests-page__lead">
              {pageLead}
            </p>
          </div>
          <div className="requests-page__heading-actions">
            <BackHomeButton />
          </div>
        </header>
        {content}
      </div>
      <RequestObservationsModal
        open={Boolean(selectedRequestForObservations)}
        request={selectedRequestForObservations}
        onClose={() => setSelectedRequestForObservations(null)}
      />
    </>
  );
};

export default RequestsExplorer;
